import React, { useEffect, useState, useRef } from "react";
import { Link, useParams } from "react-router-dom";
import { api, fileUrl, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import ProductCard from "@/components/ProductCard";
import { Star, MessageCircle, Flag, Ban, UserPlus, UserCheck, Share2 } from "lucide-react";
import { shareOrCopy } from "@/lib/utils";
import { toast } from "sonner";
import ReportDialog from "@/components/ReportDialog";

export default function SellerProfile() {
  const { id: idParam, slug } = useParams();
  const { user } = useAuth();
  const [id, setId] = useState(idParam || null);
  const [notFound, setNotFound] = useState(false);
  const [seller, setSeller] = useState(null);
  const [products, setProducts] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [reviewForm, setReviewForm] = useState({ rating: 5, comment: "" });
  const [showReport, setShowReport] = useState(false);
  const [isBlocked, setIsBlocked] = useState(false);
  const [isFollowing, setIsFollowing] = useState(false);
  const mutatedRef = useRef(false);
  const followMutatedRef = useRef(false);

  useEffect(() => {
    if (idParam) { setId(idParam); return; }
    if (slug) {
      api.get(`/shops/${slug}`)
        .then(r => setId(r.data.user_id))
        .catch(() => setNotFound(true));
    }
  }, [idParam, slug]);

  useEffect(() => {
    if (!id) return;
    api.get(`/users/${id}`).then(r => setSeller(r.data));
    api.get(`/products?seller_id=${id}`).then(r => setProducts(r.data));
    api.get(`/reviews/${id}`).then(r => setReviews(r.data));
    if (!sessionStorage.getItem(`fripri_visited_${id}`)) {
      sessionStorage.setItem(`fripri_visited_${id}`, "1");
      api.post(`/users/${id}/visit`).catch(() => {});
    }
  }, [id]);

  useEffect(() => {
    if (user && user.user_id !== id) {
      api.get("/blocks").then(r => { if (!mutatedRef.current) setIsBlocked(r.data.includes(id)); }).catch(()=>{});
      api.get("/follows").then(r => { if (!followMutatedRef.current) setIsFollowing(r.data.includes(id)); }).catch(()=>{});
    }
  }, [user, id]);

  const toggleFollow = async () => {
    if (!user) { toast.error("Connecte-toi pour suivre ce vendeur"); return; }
    followMutatedRef.current = true;
    try {
      const { data } = await api.post(`/follow/${id}`);
      setIsFollowing(data.following);
      toast.success(data.following ? "🔔 Tu suis ce vendeur — tu seras prévenu de ses lives !" : "Tu ne suis plus ce vendeur");
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const toggleBlock = async () => {
    mutatedRef.current = true;
    try {
      if (isBlocked) {
        await api.delete(`/blocks/${id}`);
        setIsBlocked(false);
        toast.success("Utilisateur débloqué");
      } else {
        await api.post(`/blocks/${id}`);
        setIsBlocked(true);
        toast.success("Utilisateur bloqué — il ne pourra plus vous contacter");
      }
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const [replyingTo, setReplyingTo] = useState(null);
  const [replyText, setReplyText] = useState("");

  const submitReply = async (reviewId) => {
    if (!replyText.trim()) { toast.error("Écris ta réponse d'abord"); return; }
    try {
      await api.post(`/reviews/${reviewId}/reply`, { reply: replyText.trim() });
      toast.success("Réponse publiée ✓");
      setReplyingTo(null);
      setReplyText("");
      const { data } = await api.get(`/reviews/${id}`);
      setReviews(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const shareShop = async () => {
    const url = `${API}/s/${seller.shop_slug || id}`;
    const text = seller.is_pro
      ? `Découvre la boutique ${seller.name} sur Fripri 💚`
      : `Découvre le dressing de ${seller.name} sur Fripri 💚`;
    const res = await shareOrCopy({ title: `Boutique ${seller.name}`, text, url });
    if (res === "copied") toast.success("Lien de la boutique copié ! 📋");
    else if (res === "failed") toast.error("Impossible de copier le lien");
  };

  const submitReview = async (e) => {
    e.preventDefault();
    if (!user) { toast.error("Connecte-toi"); return; }
    try {
      await api.post("/reviews", { seller_id: id, rating: reviewForm.rating, comment: reviewForm.comment });
      const r = await api.get(`/reviews/${id}`); setReviews(r.data);
      const s = await api.get(`/users/${id}`); setSeller(s.data);
      setReviewForm({ rating: 5, comment: "" });
      toast.success("Avis publié ✓");
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  if (notFound) return <div className="p-20 text-center font-black" data-testid="shop-not-found">Boutique introuvable 😕</div>;
  if (!seller) return <div className="p-20 text-center">Chargement…</div>;

  return (
    <div className="max-w-[1400px] mx-auto px-4 lg:px-8 py-6 lg:py-10 fade-in has-bottom-nav" data-testid="seller-profile">
      {seller.is_pro && seller.shop_banner && (
        <div className="mb-8 border-2 border-black overflow-hidden" data-testid="shop-banner">
          <img src={fileUrl(seller.shop_banner)} className="w-full h-40 lg:h-64 object-cover" alt={`Boutique de ${seller.name}`} />
        </div>
      )}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-12">
        <div className="lg:col-span-1">
          <div className="aspect-square overflow-hidden border-2 border-black bg-[color:var(--color-surface)] mb-4">
            {seller.picture ? <img src={seller.picture} className="w-full h-full object-cover" alt=""/> : <div className="w-full h-full flex items-center justify-center text-7xl font-black">{seller.name?.[0]}</div>}
          </div>
          <p className="label">Vendeur</p>
          <h1 className="text-3xl font-black tracking-tighter mt-2 mb-3 flex items-center gap-2 flex-wrap">
            {seller.name}
            {seller.is_pro && <span className="pill pill-mint text-xs" data-testid="pro-badge">PRO ✓</span>}
          </h1>
          <div className="flex items-center gap-1 mb-4">
            <Star size={14} fill="#FFE600" strokeWidth={0}/>
            <span className="font-bold">{seller.rating?.toFixed(1) || "Nouveau"}</span>
            <span className="text-sm text-[color:var(--color-text-muted)]">· {seller.review_count || 0} avis</span>
          </div>
          <button onClick={shareShop} className="btn btn-ghost w-full mb-3" data-testid="share-shop-btn">
            <Share2 size={16} strokeWidth={2.5}/> Partager la boutique
          </button>
          {seller.is_pro && seller.shop_headline && <p className="font-black text-lg leading-snug mb-2" data-testid="shop-headline">{seller.shop_headline}</p>}
          {seller.is_pro && seller.shop_description && <p className="text-sm leading-relaxed text-[color:var(--color-text-muted)] mb-4 whitespace-pre-line" data-testid="shop-description">{seller.shop_description}</p>}
          {seller.bio && <p className="text-sm leading-relaxed text-[color:var(--color-text-muted)] mb-4">{seller.bio}</p>}
          {(!user || user.user_id !== id) && (
            <>
              {!isBlocked && (
                <div className="space-y-2">
                  {user ? (
                    <Link to={`/messages?to=${id}`} className="btn btn-primary w-full" data-testid="msg-seller-btn">
                      <MessageCircle size={16} strokeWidth={2.5}/> Contacter
                    </Link>
                  ) : (
                    <Link to="/login" className="btn btn-primary w-full" data-testid="msg-seller-btn">
                      <MessageCircle size={16} strokeWidth={2.5}/> Contacter
                    </Link>
                  )}
                  <button onClick={toggleFollow} className={`btn w-full ${isFollowing ? "bg-[color:var(--color-mint)] border-2 border-black" : "border-2 border-black bg-white hover:bg-[color:var(--color-mint)]"}`} data-testid="follow-seller-btn">
                    {isFollowing ? <UserCheck size={16}/> : <UserPlus size={16}/>} {isFollowing ? "Abonné ✓" : "Suivre"}
                  </button>
                </div>
              )}
              {user && (
              <div className="flex gap-3 mt-3">
                <button onClick={()=>setShowReport(true)} className="text-xs text-[color:var(--color-text-muted)] inline-flex items-center gap-1 hover:text-[color:var(--color-coral)] font-bold uppercase tracking-wider" data-testid="report-seller-btn">
                  <Flag size={12}/> Signaler
                </button>
                <button onClick={toggleBlock} className={`text-xs inline-flex items-center gap-1 font-bold uppercase tracking-wider ${isBlocked ? "text-[color:var(--color-coral)]" : "text-[color:var(--color-text-muted)] hover:text-[color:var(--color-coral)]"}`} data-testid="block-seller-btn">
                  <Ban size={12}/> {isBlocked ? "Débloquer" : "Bloquer"}
                </button>
              </div>
              )}
            </>
          )}
        </div>

        <div className="lg:col-span-2">
          <p className="label mb-2">Articles en vente ({products.filter(p=>p.status==='active').length})</p>
          <h2 className="text-2xl lg:text-3xl font-black tracking-tighter mb-6">Le vestiaire de @{seller.name?.toLowerCase().split(" ")[0]}</h2>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
            {products.length === 0 ? <p className="text-[color:var(--color-text-muted)]">Aucun article pour l'instant.</p> :
              products.map(p => <ProductCard key={p.product_id} product={p}/>)
            }
          </div>
        </div>
      </div>

      <div className="hairline-t pt-10">
        <h2 className="text-2xl lg:text-3xl font-black tracking-tighter mb-6">Avis ({reviews.length})</h2>
        {user && user.user_id !== id && (
          <form onSubmit={submitReview} className="mb-8 p-5 border-2 border-black bg-white">
            <p className="label font-bold mb-3">Laisser un avis</p>
            <div className="flex gap-1 mb-4">
              {[1,2,3,4,5].map(r => (
                <button type="button" key={r} onClick={()=>setReviewForm({...reviewForm, rating: r})} data-testid={`rate-${r}`}>
                  <Star size={26} fill={r<=reviewForm.rating ? "#FFE600" : "transparent"} strokeWidth={2} color="#000"/>
                </button>
              ))}
            </div>
            <textarea rows="3" className="field mb-3" placeholder="Ton expérience…" value={reviewForm.comment} onChange={e=>setReviewForm({...reviewForm, comment: e.target.value})} data-testid="review-comment"/>
            <button className="btn btn-mint" type="submit" data-testid="submit-review-btn">Publier l'avis</button>
          </form>
        )}
        <div className="space-y-4">
          {reviews.map(r => (
            <div key={r.review_id} className="p-4 border-2 border-black bg-white">
              <div className="flex items-center justify-between mb-2">
                <p className="font-bold">{r.buyer_name}</p>
                <div className="flex gap-0.5">
                  {[1,2,3,4,5].map(n => <Star key={n} size={12} fill={n<=r.rating?"#FFE600":"transparent"} strokeWidth={1.5} color="#000"/>)}
                </div>
              </div>
              {r.comment && <p className="text-sm text-[color:var(--color-text-muted)]">{r.comment}</p>}
              <p className="label mt-2">{new Date(r.created_at).toLocaleDateString("fr-FR")}</p>
              {r.reply && (
                <div className="mt-3 ml-4 p-3 bg-[color:var(--color-mint)] border-2 border-black" data-testid={`review-reply-${r.review_id}`}>
                  <p className="label font-bold mb-1">Réponse du vendeur</p>
                  <p className="text-sm">{r.reply}</p>
                </div>
              )}
              {!r.reply && user?.user_id === id && (
                replyingTo === r.review_id ? (
                  <div className="mt-3 ml-4">
                    <textarea rows="2" className="field mb-2" placeholder="Ta réponse publique…" value={replyText} onChange={e=>setReplyText(e.target.value)} data-testid={`reply-input-${r.review_id}`}/>
                    <div className="flex gap-2">
                      <button className="btn btn-mint text-xs px-3 py-2" onClick={()=>submitReply(r.review_id)} data-testid={`send-reply-${r.review_id}`}>Répondre</button>
                      <button className="btn btn-ghost text-xs px-3 py-2" onClick={()=>{setReplyingTo(null); setReplyText("");}}>Annuler</button>
                    </div>
                  </div>
                ) : (
                  <button className="mt-2 text-xs font-bold underline" onClick={()=>{setReplyingTo(r.review_id); setReplyText("");}} data-testid={`reply-btn-${r.review_id}`}>
                    Répondre publiquement
                  </button>
                )
              )}
            </div>
          ))}
        </div>
      </div>
      {showReport && (
        <ReportDialog targetType="user" targetId={id} targetLabel={seller.name} onClose={()=>setShowReport(false)}/>
      )}
    </div>
  );
}
